/**
 * Supabase Admin Repository
 * Handles admin operations via Supabase Edge Functions
 */

import type {
  IAdminRepository,
  SMSBroadcastResult,
} from '../interfaces/IAdminRepository'
import { requireSupabaseClient } from '@/lib/supabase'

/**
 * Custom error for rate limiting
 */
export class RateLimitError extends Error {
  retryAfter: number
  limit: number

  constructor(message: string, retryAfter: number, limit: number) {
    super(message)
    this.name = 'RateLimitError'
    this.retryAfter = retryAfter
    this.limit = limit
  }
}

export class SupabaseAdminRepository implements IAdminRepository {
  /**
   * Send SMS broadcast via Edge Function
   */
  async sendSMSBroadcast(
    message: string,
    phoneNumbers: string[],
    useSubscribers?: boolean
  ): Promise<SMSBroadcastResult> {
    const supabase = requireSupabaseClient()

    // Ensure we have a valid session
    const {
      data: { session },
      error: sessionError,
    } = await supabase.auth.getSession()

    if (sessionError || !session) {
      throw new Error('Not authenticated. Please log in again.')
    }

    // Call Edge Function with explicit auth header
    const { data, error } = await supabase.functions.invoke('send-sms', {
      body: { message, phoneNumbers, useSubscribers },
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
    })

    if (error) {
      // Check if it's a rate limit error (429)
      if (
        error.message?.includes('Rate limit') ||
        data?.error === 'Rate limit exceeded'
      ) {
        throw new RateLimitError(
          data?.message || 'Too many SMS broadcasts. Please wait a moment.',
          data?.retryAfter || 60,
          data?.limit || 5
        )
      }
      throw new Error(`Failed to send SMS broadcast: ${error.message}`)
    }

    // Also check for rate limit in the response data
    if (data?.error === 'Rate limit exceeded') {
      throw new RateLimitError(
        data.message || 'Too many SMS broadcasts. Please wait a moment.',
        data.retryAfter || 60,
        data.limit || 5
      )
    }

    return {
      success: data.success,
      messageCount: data.messageCount || 0,
      recipientCount: data.recipientCount || 0,
      sentAt: new Date(),
      results: data.results,
    }
  }

  /**
   * Check if user is admin
   */
  async isAdmin(userId: string): Promise<boolean> {
    const supabase = requireSupabaseClient()

    const { data, error } = await supabase
      .from('profiles')
      .select('is_admin')
      .eq('id', userId)
      .single<{ is_admin: boolean }>()

    if (error) {
      throw new Error(`Failed to check admin status: ${error.message}`)
    }

    return data?.is_admin || false
  }
}
